import React, { useState, useEffect } from "react";
import "./addUser.css";

interface UserData {
  id?: number;
  userId: string;
  role: string;
}

interface AddUserProps {
  isOpen: boolean;
  onClose: () => void;
  editMode?: boolean;
  userData?: UserData;
}

const AddUser: React.FC<AddUserProps> = ({
  isOpen,
  onClose,
  editMode = false,
  userData,
}) => {
  const [userRole, setUserRole] = useState<"user" | "admin">("user");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    if (editMode && userData) {
      setUsername(userData.userId);
      setUserRole(userData.role.toLowerCase() === "admin" ? "admin" : "user");
      setPassword("");
      setConfirmPassword("");
    } else {
      setUsername("");
      setPassword("");
      setConfirmPassword("");
      setUserRole("user");
    }
  }, [editMode, userData, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editMode) {
      console.log("Edit user:", {
        id: userData?.id,
        username,
        password,
        confirmPassword,
        userRole,
      });
    } else {
      console.log("Add user:", {
        username,
        password,
        confirmPassword,
        userRole,
      });
    }
    onClose();
  };

  const handleCancel = () => {
    setUsername("");
    setPassword("");
    setConfirmPassword("");
    setUserRole("user");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="add-user-overlay" onClick={onClose}>
      <div className="add-user-wrapper">
        <button className="add-user-close" onClick={onClose}>
          ×
        </button>
        <div className="add-user-modal" onClick={(e) => e.stopPropagation()}>
          <div className="add-user-content">
            <h2 className="add-user-title">User Management</h2>
            <p className="add-user-description">Add, Edit or Delete users</p>

            <div className="add-user-form-box">
              <h3 className="add-user-form-title">
                {editMode ? "Edit User" : "Add New User"}
              </h3>

              <div className="add-user-role-buttons">
                <button
                  className={`add-user-role-btn ${
                    userRole === "user" ? "active" : ""
                  }`}
                  onClick={() => setUserRole("user")}
                >
                  User
                </button>
                <button
                  className={`add-user-role-btn ${
                    userRole === "admin" ? "active" : ""
                  }`}
                  onClick={() => setUserRole("admin")}
                >
                  Admin
                </button>
              </div>

              <form className="add-user-form" onSubmit={handleSubmit}>

                <div className="add-user-input-group">
                  <input
                    type="text"
                    placeholder="Username *"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="add-user-input"
                    required
                  />
                </div>

                <div className="add-user-input-group">
                  <input
                    type="password"
                    placeholder={
                      editMode
                        ? "New Password (leave empty to keep current)"
                        : "Password *"
                    }
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="add-user-input"
                    required={!editMode}
                  />
                </div>

                <div className="add-user-input-group">
                  <input
                    type="password"
                    placeholder={
                      editMode ? "Confirm New Password" : "Confirm Password *"
                    }
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="add-user-input"
                    required={!editMode}
                  />
                </div>
              </form>
            </div>

            <div className="add-user-actions">
              <button className="add-user-cancel-btn" onClick={handleCancel}>
                ← Cancel
              </button>
              <button className="add-user-save-btn" onClick={handleSubmit}>
                {editMode ? "Update User" : "Save User"} →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUser;
